import type { Key, ReactNode } from 'react';

import { cx } from './shared';

export interface TableColumn<Row> {
  key: string;
  header: ReactNode;
  compactLabel?: string;
  render: (row: Row) => ReactNode;
  isRowHeader?: boolean;
  className?: string;
}

export interface TableProps<Row> {
  caption: ReactNode;
  columns: readonly TableColumn<Row>[];
  rows: readonly Row[];
  getRowKey: (row: Row) => Key;
  density?: 'comfortable' | 'compact';
  compactMode?: 'stack' | 'scroll';
  emptyState?: ReactNode;
  className?: string;
}

export function Table<Row>({
  caption,
  columns,
  rows,
  getRowKey,
  density = 'comfortable',
  compactMode = 'stack',
  emptyState = 'No rows are available in this scope.',
  className,
}: TableProps<Row>) {
  const cellDensity =
    density === 'compact'
      ? 'min-h-row-compact px-2 py-1 text-meta'
      : 'min-h-row-comfortable px-3 py-2 text-body';

  return (
    <div
      data-density={density}
      className={cx(
        'rounded-surface border border-border-subtle bg-surface',
        compactMode === 'scroll' && 'overflow-x-auto',
        className,
      )}
    >
      {compactMode === 'scroll' ? (
        <p className="border-b border-border-subtle bg-surface-recessed px-3 py-2 text-meta text-ink-muted sm:hidden">
          Scroll horizontally to compare all columns.
        </p>
      ) : null}
      <table
        className={cx(
          'w-full border-separate border-spacing-0 text-left text-ink',
          compactMode === 'stack' && 'so-table-stack',
          compactMode === 'scroll' && 'min-w-max',
        )}
      >
        <caption className="px-3 py-3 text-left text-subsection-title font-semibold text-ink-strong">
          {caption}
        </caption>
        <thead className="bg-surface-recessed text-control text-ink-muted">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={cx(
                  'border-y border-border-subtle px-3 py-2 font-semibold first:border-l-0 last:border-r-0',
                  column.className,
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-3 py-6 text-body text-ink-muted">
                {emptyState}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={getRowKey(row)}
                className="bg-surface even:bg-surface-recessed/35 hover:bg-accent-soft/35"
              >
                {columns.map((column) => {
                  const label =
                    column.compactLabel ??
                    (typeof column.header === 'string' ? column.header : column.key);
                  const content = column.render(row);
                  return column.isRowHeader ? (
                    <th
                      key={column.key}
                      scope="row"
                      data-label={label}
                      className={cx(
                        'border-b border-border-subtle font-semibold text-ink-strong',
                        cellDensity,
                        column.className,
                      )}
                    >
                      {content}
                    </th>
                  ) : (
                    <td
                      key={column.key}
                      data-label={label}
                      className={cx(
                        'border-b border-border-subtle align-middle',
                        cellDensity,
                        column.className,
                      )}
                    >
                      {content}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
